/**
 * MODULE: BrainAlertsNavBadge.tsx
 * PURPOSE: Top-nav advisor alerts counter with dropdown list (quota gaps, deal risk, session deltas).
 * RECENT: 2026-07-08 [2.2.1550] — Top-nav advisor alerts; risk welcome removed from assistant landing.
 * AGENT SAFETY: SAFE
 * FILE_SIZE: 175 / 200 lines
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Badge } from 'flowbite-react';
import type {
  AssistantContextAction,
  AssistantRiskAlert,
} from '@shared/lib/frontend/api/assistantContextApi';
import { BRAZN_BRAIN_ACTIVITY_CHANGED } from '@shared/lib/frontend/utils/brainActivityEvents';
import { resolveAssistantContextAction } from '@shared/lib/frontend/utils/assistantContextActionHandlers';
import { requestOpenEmailTemplateCreate } from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import { requestOpenSalesTargetSet } from '@shared/lib/frontend/utils/salesTargetSetBridge';
import { dispatchAssistantOpenWithPrefill } from '../../design-system/components/AiAssistant/assistantPrefillBridge';
import { entityPath } from '../assistant/assistantContextEntityLinks';
import {
  normalizeRiskAlertAction,
  riskAlertActionLabel,
} from '../assistant/riskAlertActionUtils';
import { useAssistantContext } from '../../hooks/assistant/useAssistantContext';
import {
  brainAlertsNavBadgeCount,
  brainAlertsNavVisibleAlerts,
} from './brainAlertsNavCount';

function severityBadge(severity?: string): 'failure' | 'warning' | 'info' {
  if (severity === 'attention') return 'failure';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function severityLabel(severity?: string): string {
  if (severity === 'attention') return 'Needs attention';
  if (severity === 'warning') return 'Watch';
  return 'Update';
}

function primaryAlertAction(alert: AssistantRiskAlert): AssistantContextAction | null {
  const actions = alert.suggested_actions ?? [];
  for (const action of actions) {
    const label = riskAlertActionLabel(action);
    if (!label) continue;
    return normalizeRiskAlertAction(
      action,
      alert.entity_type && alert.entity_id
        ? { entity_type: alert.entity_type, entity_id: alert.entity_id }
        : undefined,
    );
  }
  return null;
}

export function BrainAlertsNavBadge() {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const { context, refresh } = useAssistantContext({
    enabled: true,
    debounceMs: 800,
    followUpRefreshDelaysMs: [5000, 15000],
  });

  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener(BRAZN_BRAIN_ACTIVITY_CHANGED, onRefresh);
    return () => window.removeEventListener(BRAZN_BRAIN_ACTIVITY_CHANGED, onRefresh);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const alerts = brainAlertsNavVisibleAlerts(context);
  const count = brainAlertsNavBadgeCount(context);

  const handleContextAction = useCallback(
    (action: AssistantContextAction) => {
      const resolved = resolveAssistantContextAction(action);
      if (resolved.kind === 'navigate') {
        navigate(resolved.path);
        setOpen(false);
        return;
      }
      if (resolved.kind === 'open_email_template_create') {
        requestOpenEmailTemplateCreate({ coachInAssistant: true });
        setOpen(false);
        return;
      }
      if (resolved.kind === 'open_sales_target_set') {
        requestOpenSalesTargetSet({ coachInAssistant: true });
        setOpen(false);
        return;
      }
      const prompt = resolved.text.trim();
      if (!prompt) return;
      const autoSend = resolved.kind === 'send_prompt' || resolved.kind === 'prompt';
      dispatchAssistantOpenWithPrefill(prompt, { autoSend });
      setOpen(false);
    },
    [navigate],
  );

  const onAlertClick = useCallback(
    (alert: AssistantRiskAlert) => {
      const path =
        alert.entity_type && alert.entity_id
          ? entityPath({ entity_type: alert.entity_type, entity_id: alert.entity_id })
          : null;
      if (path) {
        navigate(path);
        setOpen(false);
        return;
      }
      const prompt = (alert.suggested_prompts?.[0] || alert.title || '').trim();
      if (prompt) {
        dispatchAssistantOpenWithPrefill(prompt, { autoSend: true });
        setOpen(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (count <= 0) setOpen(false);
  }, [count]);

  if (count <= 0) return null;

  const preview = alerts.slice(0, 6);
  const badgeLabel = count > 99 ? '99+' : String(count);
  const label = count === 1 ? '1 advisor alert' : `${count} advisor alerts`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-full border border-rose-300/80 bg-gradient-to-r from-rose-100 to-orange-100 px-2.5 py-1 text-xs font-semibold text-rose-900 shadow-sm transition hover:from-rose-200 hover:to-orange-200 dark:border-rose-700/60 dark:from-rose-950/70 dark:to-orange-950/50 dark:text-rose-100 dark:hover:from-rose-900/80 dark:hover:to-orange-900/60"
        aria-label={`${label} — open advisor alerts`}
        aria-expanded={open}
        title={label}
        data-testid="topbar-brain-alerts-badge"
        onClick={() => setOpen((value) => !value)}
      >
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-600 dark:text-rose-300" aria-hidden />
        <span className="tabular-nums">{badgeLabel}</span>
      </button>

      {open ? (
        <div
          className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-rose-200 bg-white py-2 shadow-lg dark:border-rose-900/50 dark:bg-gray-800"
          data-testid="topbar-brain-alerts-panel"
        >
          <div className="border-b border-rose-100 px-3 pb-2 dark:border-rose-900/40">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Advisor alerts</p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Pipeline signals Brazn surfaced before you ask.
            </p>
          </div>

          <ul className="max-h-72 overflow-y-auto" aria-label="Advisor alerts">
            {preview.map((alert, idx) => {
              const action = primaryAlertAction(alert);
              const actionLabel = action ? riskAlertActionLabel(action) : '';
              return (
                <li key={`${alert.title}-${idx}`}>
                  <button
                    type="button"
                    className="flex w-full flex-col items-start gap-1 px-3 py-2.5 text-left transition hover:bg-rose-50/80 dark:hover:bg-rose-950/30"
                    data-testid="topbar-brain-alert-item"
                    onClick={() => onAlertClick(alert)}
                  >
                    <Badge color={severityBadge(alert.severity)} size="xs">
                      {severityLabel(alert.severity)}
                    </Badge>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {alert.title}
                    </span>
                    {alert.body && alert.body !== alert.title ? (
                      <span className="line-clamp-2 text-xs text-gray-600 dark:text-gray-300">
                        {alert.body}
                      </span>
                    ) : null}
                  </button>
                  {actionLabel && action ? (
                    <div className="px-3 pb-2">
                      <button
                        type="button"
                        className="rounded-full border border-blue-300/80 bg-blue-50/90 px-2.5 py-0.5 text-[10px] font-medium text-blue-900 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100 dark:hover:bg-blue-900/50"
                        data-testid="topbar-brain-alert-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleContextAction(action);
                        }}
                      >
                        {actionLabel}
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <div className="border-t border-rose-100 px-3 pt-2 dark:border-rose-900/40">
            <Link
              to="/langflow-assistant"
              data-testid="topbar-brain-alerts-view-all"
              className="block text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
              onClick={() => setOpen(false)}
            >
              Open in Assistant
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
